import React, { FC } from 'react'
import NextHead from 'next/head'
import Script from 'next/script'
import { useRouter } from 'next/router'
import { DefaultSeo } from 'next-seo'
import configEN from '@config/seo-EN.json'
import configDE from '@config/seo-DE.json'

const Head: FC = () => {
  const { locale = 'de' } = useRouter()

  const googleTagManagerSnippet = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;
    j.addEventListener('load', function() {});
f.parentNode.insertBefore(j,f);
    })
    (window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GT_ID}');
  `

  const elevarSnippet = `
  try {
          const settings = {};
          const config = (await import("https://shopify-gtm-suite.getelevar.com/configs/b05c98e50a0b3879de67f690478042dd1af5841a/config.js")).default;
          const scriptUrl = settings.proxyPath
          ? settings.proxyPath + config.script_src_custom_pages_proxied
          : config.script_src_custom_pages;

          if (scriptUrl) {
          const { handler } = await import(scriptUrl);
          await handler(config, settings);
        }
        } catch (error) {
          console.error("Elevar Error:", error);
        }
  `

  const tapfiliateSnippet = `
        (function(t,a,p){t.TapfiliateObject=a;t[a]=t[a]||function(){ (t[a].q=t[a].q||[]).push(arguments)}})(window,'tap');
        tap('create', '49841-9efa2e', { integration: "javascript" });
        tap('detect');
  `


  const redditPixelSnippet = `
    !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init','${ process.env.NEXT_PUBLIC_REDDIT_ID }', {"optOut":false,"useDecimalCurrencyValues":true});rdt('track', 'PageVisit');
  `

  const reamazeSettings = `
  var _support = _support || { 'ui': {}, 'user': {} };
  _support['account'] = 'remihealth';
  _support['ui']['contactMode'] = 'default';
  _support['ui']['enableKb'] = 'false';
  _support['ui']['styles'] = {
    widgetColor: 'rgb(250, 203, 0)',
  };
  _support['ui']['shoutboxFacesMode'] = "brand-avatar";
  _support['ui']['shoutboxHeaderLogo'] = true;
  _support['ui']['widget'] = {
    icon: 'chat',
    displayOn: 'all',
    allowBotProcessing: true,
    label: false,
    position: 'bottom-left',
    size: 58,
    mobilePosition: 'bottom-left',
    mobileSize: 40
  };
  _support['apps'] = {
    faq: {"enabled":false},
    recentConversations: {},
    orders: {"enabled":false}
  };
  `

  const reamazeEventHandler = `
  var _support = _support || { 'ui': {}, 'user': {} };
  _support.outbounds = _support.outbounds || [];
  _support.outbounds.push({
    id: "55038",
    name: "Remi",
    message: "Herzlich Willkommen bei Remi. In welcher Sprache k&#246;nnen wir dir weiterhelfen?",
    sound: false,
    transient: true,
    duration: 0,
    botsEnabled: true,
    enableCustomFields: false,
    disableCueClick: false,
    user: {
      type: "team"
    },
    buttons: [
      {
        text: "English",
        response: true
      },
      {
        text: "Deutsch",
        response: true
      },
    ],
    rules: [
      {
        type: "customerLocation",
        op: "equals",
        value: "DE",
      },
    ],
  });
  `

  const seoConfig = locale === 'de' ? configDE : configEN
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.myremi.com'
  const resolvedSeo = {
    ...seoConfig,
    openGraph: {
      ...seoConfig.openGraph,
      images: seoConfig.openGraph?.images?.map((img: any) => ({
        ...img,
        url: img.url?.startsWith('http') ? img.url : `${baseUrl}${img.url?.startsWith('/') ? '' : '/'}${img.url}`
      })) ?? seoConfig.openGraph?.images
    }
  }
  const languageAlternates = [
    { hrefLang: 'de', href: `${baseUrl}/de` },
    { hrefLang: 'en', href: `${baseUrl}/en` },
    { hrefLang: 'x-default', href: `${baseUrl}/de` }
  ]

  // @ts-ignore
  return (
    <>
      <DefaultSeo {...resolvedSeo} languageAlternates={languageAlternates} />
      <NextHead>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#edf4be"/>
        <link rel="manifest" href="/site.webmanifest" key="site-manifest" />
        {/* Preconnect only for critical first request; use ≤4 (Lighthouse). Sanity not preconnected: images go via _next/image (same-origin). */}
        {process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_DOMAIN && (
          <link
            rel="preconnect"
            href={
              (process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_DOMAIN || '').startsWith('http')
                ? process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_DOMAIN
                : `https://${process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_DOMAIN}`
            }
          />
        )}
        <link rel="dns-prefetch" href="https://cdn.sanity.io" />
        <link rel="dns-prefetch" href="https://remihealth.reamaze.io" />
        <link rel="dns-prefetch" href="https://cdn.consentmanager.net" />
        {/* Font preloads removed: bundled CSS resolves font URLs to different paths, so preload was never used and triggered warnings */}
      </NextHead>

      {/* Scripts: lazyOnload keeps analytics/tracking out of critical path so they don't delay LCP */}
      <Script id="gtag-base" type="text/plain" className="cmplazyload" data-cmp-vendor="s26" strategy="lazyOnload">
        {googleTagManagerSnippet}
      </Script>
      <Script id="elevar-snippet" type="module" strategy="lazyOnload">
        {elevarSnippet}
      </Script>
      <Script id="tapfiliate-snippet" type="module" strategy="lazyOnload">
        {tapfiliateSnippet}
      </Script>
      <Script type="text/plain" className="cmplazyload" data-cmp-vendor="c31589" id="reamaze-settings" strategy="lazyOnload">
        {reamazeSettings}
      </Script>
      <Script type="text/plain" className="cmplazyload" data-cmp-vendor="c31589" id="reamaze-event-handler" strategy="lazyOnload">
        {reamazeEventHandler}
      </Script>

      {/* <!-- Google Tag Manager (noscript) --> */}
      <noscript id="gtag-noscript">
        <iframe src={`https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_GT_ID}`}
                height="0" width="0" style={{ display: 'none', visibility: 'hidden' }}></iframe>
      </noscript>
      {/* <!-- End Google Tag Manager (noscript) --> */}
    </>
  )
}

export default Head
